/* eslint-disable max-lines */
import axios from 'axios';
import createDebounced from '~/lib/debounce';
// ---- external imports ----

import { getBaseUrl } from '../lib/helpers';
// ---- parent imports ----

import { getRequestHeaders } from './headers';
import { formatResponse } from './utlis';
import ApiService from './axiosInterceptor.service';
import {
  GAME_USER_POST_APIS,
  GAME_USER_PATCH_APIS,
} from '../endpoints';
// ---- local imports ----

class GameService {
    constructor() {}

    async updateTurn(gameUserId, gamePlayStats, oldTurnNumber, oldTurnPhase) {
        const data = {
            gamePlayStats,
            oldTurnNumber,
            oldTurnPhase,
        };

        const response = await ApiService.request({
            method: 'PATCH',
            headers: getRequestHeaders(),
            url: `${getBaseUrl()}/game_users/${gameUserId}/${GAME_USER_PATCH_APIS.UPDATE_TURN}`,
            data,
            waitFor: ['SELL_PRODUCT', 'UPDATE_SELLING_PRICES'],
        });

        return formatResponse(response);
    }

    async createNew(userId, characterId, storeId, selectionTiming) {

        const data = {
            'game_user': {
                'user_id': userId,
                'character_id': characterId,
                'store_id': storeId,
            },
            'selection_screen_timing': selectionTiming,
        };

        const response = await ApiService.request({
            method: 'POST',
            headers: getRequestHeaders(),
            data: JSON.stringify(data),
            url: `${getBaseUrl()}/game_users`
        });

        return formatResponse(response);
    }

    async load(gameUserId) {
        const response = await ApiService.request({
            method: "GET",
            url: `${getBaseUrl()}/game_users/${gameUserId}`,
            headers: getRequestHeaders(),
            // A save that is gone is let go of by the load thunk, not the modal.
            isNotFoundHandledByCaller: true,
        });

        return formatResponse(response);
    }

    async getGameUsersForUser(userId) {
        const response = await axios({
            method: "GET",
            url: `${getBaseUrl()}/game_users/${userId}/game_user_ids_for_user`,
            headers: getRequestHeaders(),
        });

        return formatResponse(response);
    }

    async getLoadGameDataForUser(userId) {
        const response = await axios({
            method: "GET",
            url: `${getBaseUrl()}/game_users/${userId}/get_load_game_data_for_user`,
            headers: getRequestHeaders(),
        });

        return formatResponse(response);
    }

    async updateProductsSellingPrice(gameUserId, productsSellingPrices) {
        const data = { products: productsSellingPrices };

        const response = await ApiService.request({
            method: 'PATCH',
            headers: getRequestHeaders(),
            data: JSON.stringify(data),
            url: `${getBaseUrl()}/game_users/${gameUserId}/${GAME_USER_PATCH_APIS.UPDATE_SELLING_PRICES}`,
            requestKey: 'UPDATE_SELLING_PRICES'
        });

        return formatResponse(response);
    }

	updateProductsSellingPriceDebounced = createDebounced(
		(gameUserId, productsSellingPrices) => this.updateProductsSellingPrice(gameUserId, productsSellingPrices),
		500,
		{ latestOnly: true }
	);

    async sellProduct(
        gameUserId,
        customerId,
        customerIndex,
        productType,
        productCount,
        discount,
        isTradeshowCustomer = false,
        isCreditCustomer = false
    ) {
        // TODO: update backend to use either 'table' or 'desk' only
        productType = productType === 'table' ? 'desk' : productType;
        /* eslint-disable camelcase */
        const data = { 
            product_type: productType,
            customer_id: customerId,
            product_count: productCount,
            discount: discount,
            is_tradeshow_customer: isTradeshowCustomer,
            is_credit_customer: isCreditCustomer,
            customer_ui_index: customerIndex,
        };
        /* eslint-enable camelcase */

        const response = await ApiService.request({
            method: 'PATCH',
            headers: getRequestHeaders(),
            data: JSON.stringify(data),
            url: `${getBaseUrl()}/game_users/${gameUserId}/${GAME_USER_PATCH_APIS.SELL_PRODUCT}`,
            requestKey: 'SELL_PRODUCT',
        });

        return formatResponse(response);
    }

    async buyProducts(gameUserId, products) {
        // eslint-disable-next-line camelcase
        const data = { products_to_buy: products };

        const response = await ApiService.request({
            method: 'POST',
            headers: getRequestHeaders(),
            data: JSON.stringify(data),
            url: `${getBaseUrl()}/game_users/${gameUserId}/${GAME_USER_POST_APIS.BUY_PRODUCTS}`
        });

        return formatResponse(response);
    }

	//	needs work to handle api call
    async saveLogbook(gameUserId, updatedLogbook) {
        const data = { logbook: updatedLogbook };

        const response = await ApiService.request({
            method: 'PATCH',
            headers: getRequestHeaders(),
            data: JSON.stringify(data),
            url: `${getBaseUrl()}/game_users/${gameUserId}/${GAME_USER_PATCH_APIS.UPDATE_LOGBOOK}`
        });

        return formatResponse(response);
    }

    async saveTradeshowSalesLog(gameUserId, tradeshowSalesSummary) {
        // eslint-disable-next-line camelcase
        const data = { tradeshow_sales: tradeshowSalesSummary.toPlainObj() };

        const response = await ApiService.request({
            method: 'PATCH',
            headers: getRequestHeaders(),
            data: JSON.stringify(data),
            url: `${getBaseUrl()}/game_users/${gameUserId}/${GAME_USER_PATCH_APIS.SAVE_TRADESHOW_SALES_LOG}`
        });

        return formatResponse(response);
    }

    async getLogbook(gameUserId) {
        const response = await ApiService.request({
            method: "GET",
            url: `${getBaseUrl()}/game_users/${gameUserId}/logbook`,
            headers: getRequestHeaders()
        });

        return formatResponse(response);
    }

    async getFinancialStatements(gameUserId) {
        const response = await ApiService.request({
            method: "GET",
            url: `${getBaseUrl()}/game_users/${gameUserId}/financial_statements`,
            headers: getRequestHeaders(),
        });

        const formattedResponse = formatResponse(response);

        if (formattedResponse.isSuccess) {
            formattedResponse.data = {
                financialStatements: response.data.financial_statements,
                lostInventoryByQuarterNum: response.data.lost_inventory_by_quarter_number
            };
        }

        return formattedResponse;
    }

    async saveDistributedProfit(gameUserId, distributedProfit) {
        const data = {
            // eslint-disable-next-line camelcase
            distributed_profit: distributedProfit 
        };

        const response = await ApiService.request({
            method: 'PATCH',
            headers: getRequestHeaders(),
            data: JSON.stringify(data),
            url: `${getBaseUrl()}/game_users/${gameUserId}/${GAME_USER_PATCH_APIS.UPDATE_DISTRIBUTED_PROFITS}`
        });

        return formatResponse(response);
    }

    async updateFinancialStatementsField(gameUserId, quarterNum, field, value) {
        const data = {
            // eslint-disable-next-line camelcase
            quarterNum,
            field,
            [field]: value 
        };

        const response = await ApiService.request({
            method: 'PATCH',
            headers: getRequestHeaders(),
            data: JSON.stringify(data),
            url: `${getBaseUrl()}/game_users/${gameUserId}/${GAME_USER_PATCH_APIS.UPDATE_FINANCIAL_STATEMENTS_FIELD}`,
            requestKey: 'UPDATE_FINANCIAL_STATEMENTS_FIELD',
        });

        return formatResponse(response);
    }

    async submitFinancialStatements(gameUserId, quarterNum) {
        const data = {
            // eslint-disable-next-line camelcase
            quarterNum,
        };

        // Through the gate, not bare axios: only the gate reads `waitFor`, and
        // without it the submit could overtake a field save still in flight.
        const response = await ApiService.request({
            method: 'POST',
            headers: getRequestHeaders(),
            data: JSON.stringify(data),
            url: `${getBaseUrl()}/game_users/${gameUserId}/${GAME_USER_POST_APIS.SUBMIT_FINANCIAL_STATEMENTS}`,
            waitFor: ['UPDATE_FINANCIAL_STATEMENTS_FIELD']
        });

        return formatResponse(response);
    }

    async submitFinancialRatios(gameUserId, quarterNum) {
        const data = {
            // eslint-disable-next-line camelcase
            quarterNum,
        };

        // Through the gate, not bare axios: only the gate reads `waitFor`, and
        // without it the submit could overtake a field save still in flight.
        const response = await ApiService.request({
            method: 'POST',
            headers: getRequestHeaders(),
            data: JSON.stringify(data),
            url: `${getBaseUrl()}/game_users/${gameUserId}/${GAME_USER_POST_APIS.SUBMIT_FINANCIAL_RATIOS}`,
            waitFor: ['UPDATE_FINANCIAL_STATEMENTS_FIELD']
        });

        return formatResponse(response);
    }

    async saveStoreName(gameUserId, storeName) {
        // eslint-disable-next-line camelcase
        const data = { store_name: storeName };
        const response = await ApiService.request({
            method: 'PATCH',
            headers: getRequestHeaders(),
            data: JSON.stringify(data),
            url: `${getBaseUrl()}/game_users/${gameUserId}/${GAME_USER_PATCH_APIS.UPDATE_STORE_NAME}`
        });

        return formatResponse(response);
    }

	//	todo: remove this method as it is not used
    async savePlayerHealth(gameUserId, updatedPlayerHealth) {
        // eslint-disable-next-line camelcase
        const data = { player_health: updatedPlayerHealth };
        const response = await ApiService.request({
            method: 'PATCH',
            headers: getRequestHeaders(),
            data: JSON.stringify(data),
            url: `${getBaseUrl()}/game_users/${gameUserId}/${GAME_USER_PATCH_APIS.UPDATE_PLAYER_HEALTH}`
        });

        return formatResponse(response);
    }
	//	todo: remove checkpoint methods that are not used
    async saveCheckpoint(gameUserId) {
        const response = await ApiService.request({
            method: 'PATCH',
            headers: getRequestHeaders(),
            url: `${getBaseUrl()}/game_users/${gameUserId}/save_checkpoint`
        });

        return formatResponse(response);
    }

    async reloadCheckpoint(gameUserId) {
        const response = await ApiService.request({
            method: 'POST',
            headers: getRequestHeaders(),
            url: `${getBaseUrl()}/game_users/${gameUserId}/${GAME_USER_POST_APIS.RELOAD_CHECKPOINT}`
        });

        return formatResponse(response);
    }

    async saveNewCash(gameUserId, newCash) {
        // eslint-disable-next-line camelcase
        const data = { new_cash: newCash };
        const response = await ApiService.request({
            method: 'PATCH',
            headers: getRequestHeaders(),
            data: JSON.stringify(data),
            url: `${getBaseUrl()}/game_users/${gameUserId}/${GAME_USER_PATCH_APIS.UPDATE_CASH}`
        });

        return formatResponse(response);
    }
}

export default new GameService();
